import React, { useMemo } from 'react';
import type {
  WorkflowEvaluationJob,
  WorkflowTuningJob,
  WorkflowTuningJournal,
  WorkflowTuningUsage,
} from '../../../types/workflowTuning';
import type { CandidateVerdict } from '../../../utils/workflowTuningSpec';
import {
  stepSeries,
  type StepSeries,
  buildSliceBars,
  evaluationSeries,
  formatCompactTokens,
  formatScore,
  formatSigned,
  hasTokenSplit,
  maxTokens,
  qualityView,
  seriesScale,
  workAmount,
} from '../../../utils/workflowTuningCharts';
import { Pill } from './WorkflowFormControls';

export interface WorkflowTuningChartsProps {
  job: WorkflowTuningJob | null;
  journal: WorkflowTuningJournal | null;
  usage: WorkflowTuningUsage | null;
  /** Evaluation runs on this integration — the only quality series the contract carries. */
  evalJobs: WorkflowEvaluationJob[];
  verdict: CandidateVerdict;
}

const VERDICT_NOTE: Record<CandidateVerdict, string | null> = {
  'accepted-improved': 'The candidate was selected and quality rose.',
  'accepted-worse': 'The candidate was selected even though quality fell — the delta below is the real one.',
  rejected: 'The candidate was rejected, so the proposal holds the graph as it already is.',
  unknown: 'This run reported no selection decision, so the delta is a measurement, not a verdict.',
};

/** Rows that fit the list box before it starts scrolling — drives the "more below" edge. */
const VISIBLE_ROWS = 9;

const DIRECTION_ICON: Record<'up' | 'down' | 'flat', string> = {
  up: 'fa-arrow-trend-up',
  down: 'fa-arrow-trend-down',
  flat: 'fa-arrow-right-long',
};

/** A chart with nothing to draw says which field is missing, never a padded zero. */
const NoData: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="cwt-chart__empty">
    <i className="fas fa-chart-simple" aria-hidden="true" />
    <span>{children}</span>
  </div>
);

/**
 * Run analytics for one workflow-tuning job: quality, where the optimizer worked and
 * what it spent. Every number comes from the job, its journal or its usage record —
 * per-iteration scores are not in the contract, so quality is drawn as the before/after
 * pair it actually is rather than a fabricated curve.
 */
/**
 * Подпись оси — не то же самое, что число в карточке: под неё отведено
 * `padLeft` пикселей, и `formatScore` с четырьмя знаками после запятой при
 * шестизначном скоре давал «298284.0000», то есть одиннадцать знаков, которые
 * уезжали за левый край SVG. Здесь точность отдаётся в обмен на то, что
 * подпись видно целиком; полное значение остаётся в подсказке на точке.
 */
export function formatAxisTick(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (magnitude >= 10_000) return `${Math.round(value / 1_000)}k`;
  if (magnitude >= 100) return String(Math.round(value));
  if (magnitude >= 1) return value.toFixed(2);
  return value.toFixed(4);
}

const EvaluationTrend: React.FC<{ points: ReturnType<typeof evaluationSeries>; best?: number }> = ({ points, best }) => {
  const scale = seriesScale(points.map((point) => point.score));
  if (!scale) return null;

  // A fixed viewBox scaled to the card: the axis labels stay legible at any width.
  const W = 320;
  const H = 132;
  const padLeft = 46;
  const padRight = 10;
  const padTop = 12;
  const padBottom = 22;
  const innerW = W - padLeft - padRight;
  const innerH = H - padTop - padBottom;
  const x = (index: number) => padLeft + (points.length > 1 ? (index / (points.length - 1)) * innerW : innerW / 2);
  const y = (score: number) => padTop + (1 - scale.place(score)) * innerH;

  const line = points.map((point, index) => `${x(index)},${y(point.score)}`).join(' ');
  const area = `${padLeft},${padTop + innerH} ${line} ${x(points.length - 1)},${padTop + innerH}`;
  const bestY = typeof best === 'number' && best >= scale.min && best <= scale.max ? y(best) : null;

  return (
    <svg className="cwt-trend" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Score of each evaluation run">
      <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + innerH} className="cwt-trend__axis" />
      <line x1={padLeft} y1={padTop + innerH} x2={W - padRight} y2={padTop + innerH} className="cwt-trend__axis" />
      {points.length > 1 ? <polygon className="cwt-trend__area" points={area} /> : null}
      {points.length > 1 ? <polyline className="cwt-trend__line" points={line} /> : null}
      {points.map((point, index) => (
        <circle key={point.id} className="cwt-trend__dot" cx={x(index)} cy={y(point.score)} r={3}>
          <title>{`Run ${point.ordinal}: ${formatScore(point.score)}${point.mode ? ` · ${point.mode}` : ''}`}</title>
        </circle>
      ))}
      {bestY != null ? <line x1={padLeft} y1={bestY} x2={W - padRight} y2={bestY} className="cwt-trend__ref" /> : null}
      <text x={padLeft - 5} y={padTop + 4} className="cwt-trend__tick" textAnchor="end">
        {formatAxisTick(scale.max)}
      </text>
      <text x={padLeft - 5} y={padTop + innerH} className="cwt-trend__tick" textAnchor="end">
        {formatAxisTick(scale.min)}
      </text>
      <text x={padLeft} y={H - 6} className="cwt-trend__tick">
        run 1
      </text>
      <text x={W - padRight} y={H - 6} className="cwt-trend__tick" textAnchor="end">
        run {points.length}
      </text>
    </svg>
  );
};


/**
 * Кривая валидации по шагам прогона (AIT-292).
 *
 * Опорная точка рисуется иначе прочих: от неё считается дельта, и путать её с
 * первым шагом оптимизации нельзя. Непосчитанный шаг — разрыв линии, а не ноль:
 * подставленный ноль показал бы провал качества там, где измерения не было.
 */
const StepCurve: React.FC<{ series: StepSeries; ariaLabel?: string }> = ({
  series,
  ariaLabel = 'Validation score of each optimization step',
}) => {
  const scale = seriesScale(series.measured.map((point) => point.score as number));
  if (!scale || series.points.length === 0) return null;

  const W = 320;
  const H = 132;
  const padLeft = 46;
  const padRight = 10;
  const padTop = 12;
  const padBottom = 22;
  const innerW = W - padLeft - padRight;
  const innerH = H - padTop - padBottom;

  const first = series.points[0].iteration;
  const last = series.points[series.points.length - 1].iteration;
  const span = last - first;
  const x = (iteration: number) => padLeft + (span > 0 ? ((iteration - first) / span) * innerW : innerW / 2);
  const y = (score: number) => padTop + (1 - scale.place(score)) * innerH;

  // Разрыв рвёт линию: соседними считаются только точки без дырки между ними.
  const segments: string[] = [];
  let run: string[] = [];
  series.points.forEach((point) => {
    if (point.score === null) {
      if (run.length > 1) segments.push(run.join(' '));
      run = [];
      return;
    }
    run.push(`${x(point.iteration)},${y(point.score)}`);
  });
  if (run.length > 1) segments.push(run.join(' '));

  return (
    <svg className="cwt-trend" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel}>
      <line x1={padLeft} y1={padTop} x2={padLeft} y2={padTop + innerH} className="cwt-trend__axis" />
      <line x1={padLeft} y1={padTop + innerH} x2={W - padRight} y2={padTop + innerH} className="cwt-trend__axis" />
      {series.baseline?.score != null ? (
        <line
          x1={padLeft}
          y1={y(series.baseline.score)}
          x2={W - padRight}
          y2={y(series.baseline.score)}
          className="cwt-trend__ref"
        />
      ) : null}
      {segments.map((points, i) => (
        <polyline key={i} className="cwt-trend__line" points={points} />
      ))}
      {series.points.map((point) =>
        point.score === null ? (
          <line
            key={point.iteration}
            x1={x(point.iteration)}
            y1={padTop}
            x2={x(point.iteration)}
            y2={padTop + innerH}
            className="cwt-trend__gap"
          >
            <title>{`Step ${point.iteration}: not measured`}</title>
          </line>
        ) : (
          <circle
            key={point.iteration}
            className={point.baseline ? 'cwt-trend__dot cwt-trend__dot--baseline' : 'cwt-trend__dot'}
            cx={x(point.iteration)}
            cy={y(point.score)}
            r={point.baseline ? 4.5 : 3}
          >
            <title>
              {point.baseline
                ? `Baseline: ${formatScore(point.score)}`
                : `Step ${point.iteration}: ${formatScore(point.score)}`}
            </title>
          </circle>
        )
      )}
      <text x={padLeft - 5} y={padTop + 4} className="cwt-trend__tick" textAnchor="end">
        {formatAxisTick(scale.max)}
      </text>
      <text x={padLeft - 5} y={padTop + innerH} className="cwt-trend__tick" textAnchor="end">
        {formatAxisTick(scale.min)}
      </text>
      {/* На единственной точке обе подписи совпали бы: показываем её под самой точкой. */}
      {span > 0 ? (
        <>
          <text x={padLeft} y={H - 6} className="cwt-trend__tick">
            step {first}
          </text>
          <text x={W - padRight} y={H - 6} className="cwt-trend__tick" textAnchor="end">
            step {last}
          </text>
        </>
      ) : (
        <text x={x(first)} y={H - 6} className="cwt-trend__tick" textAnchor="middle">
          step {first}
        </text>
      )}
    </svg>
  );
};

const WorkflowTuningCharts: React.FC<WorkflowTuningChartsProps> = ({ job, journal, usage, evalJobs, verdict }) => {
  const bars = useMemo(() => buildSliceBars(journal), [journal]);
  const quality = useMemo(() => qualityView(job?.scores), [job]);
  const work = useMemo(() => workAmount(bars, job?.summary), [bars, job]);
  const split = hasTokenSplit(bars);
  const peak = maxTokens(bars);
  const spent = bars.reduce((sum, bar) => sum + bar.tokens, 0);
  const totalTokens = usage?.total?.total_tokens ?? job?.summary?.tokens ?? spent;
  const totalCost = usage?.total?.total_cost;
  const evalPoints = useMemo(() => evaluationSeries(evalJobs), [evalJobs]);
  // Точки приходят по ходу прогона, в отличие от `scores`, — поэтому кривая
  // считается всегда, а не только на завершённой джобе.
  const steps = useMemo(() => stepSeries(job), [job]);
  // Тот же ряд, только читает `quality_score`: качество само по себе, не то, по
  // чему шёл отбор. Считается всегда — гейт по метрике ниже решает, рисовать ли.
  const qualitySteps = useMemo(() => stepSeries(job, (s) => s.quality_score), [job]);
  // Когда отбор шёл по качеству, "Validation by step" уже рисует качество — второй
  // график был бы точной копией первого. Без известного metric_id (старая джоба
  // или старый бэк) график тоже не рисуется: quality_score у таких точек всё равно нет.
  const metricId = job?.metric_id;
  const showQualityChart = Boolean(metricId) && metricId !== 'quality' && qualitySteps.points.length > 0;
  const metricLabel = showQualityChart ? String(metricId).replace(/_/g, ' ') : null;

  // Nothing measured and nothing journalled — an empty analytics block is worse than none.
  // Кривая по шагам считается наравне с остальным: на живом прогоне `scores` ещё
  // нет, и без неё блок скрылся бы ровно тогда, когда смотреть на него интереснее всего.
  if (!quality && bars.length === 0 && evalPoints.length === 0 && steps.points.length === 0 && !showQualityChart) return null;

  return (
    <section className="cwt-analytics" data-testid="cwt-analytics">
      <header className="cwt-analytics__head">
        <span className="cwt-analytics__icon" aria-hidden="true">
          <i className="fas fa-chart-column" />
        </span>
        <div className="cwt-analytics__intro">
          <span className="cwt-analytics__eyebrow">Metrics</span>
          <h3>Run analytics</h3>
          <p>Quality, the states the optimizer actually rewrote, and what the run spent getting there.</p>
        </div>
        {totalTokens ? (
          <span className="cwt-analytics__total">
            <strong>{totalTokens.toLocaleString()}</strong>
            <small>tokens{typeof totalCost === 'number' ? ` · $${totalCost.toFixed(3)}` : ''}</small>
          </span>
        ) : null}
      </header>

      <div className="cwt-analytics__grid">
        <article className="cwt-chart cwt-chart--wide" data-testid="cwt-chart-steps">
          <header className="cwt-chart__head">
            <h4>Validation by step{metricLabel ? ` — ${metricLabel}` : ''}</h4>
            <span className="cwt-chart__legend">
              <span className="cwt-chart__key cwt-chart__key--ref" /> baseline
              <span className="cwt-chart__hint">step 0 is the reference, not the first optimization step</span>
            </span>
          </header>
          {steps.measured.length > 0 ? (
            <div className="cwt-chart__body">
              <StepCurve series={steps} />
              {steps.hasGaps ? (
                <p className="cwt-chart__note">
                  A step with no score is left as a break in the line — not measured is not the same as measured zero.
                </p>
              ) : null}
            </div>
          ) : (
            <NoData>
              {steps.points.length > 0
                ? 'No step was scored yet — the curve fills in as the run reports them.'
                : 'This run reported no step points. Runs started before they were recorded always come back empty.'}
            </NoData>
          )}
        </article>

        {showQualityChart ? (
          <article className="cwt-chart cwt-chart--wide" data-testid="cwt-chart-quality-steps">
            <header className="cwt-chart__head">
              <h4>Quality by step</h4>
              <span className="cwt-chart__legend">
                <span className="cwt-chart__key cwt-chart__key--ref" /> baseline
                <span className="cwt-chart__hint">selection ran on {metricLabel}, this is quality on its own</span>
              </span>
            </header>
            {qualitySteps.measured.length > 0 ? (
              <div className="cwt-chart__body">
                <StepCurve series={qualitySteps} ariaLabel="Quality score of each optimization step" />
                {qualitySteps.hasGaps ? (
                  <p className="cwt-chart__note">
                    A step with no quality score is left as a break in the line — not measured is not the same as
                    measured zero.
                  </p>
                ) : null}
              </div>
            ) : (
              <NoData>No step was scored for quality yet — the curve fills in as the run reports it.</NoData>
            )}
          </article>
        ) : null}

        <article className="cwt-chart" data-testid="cwt-chart-quality">
          <header className="cwt-chart__head">
            <h4>Quality</h4>
            <span className="cwt-chart__hint">initial → best</span>
          </header>
          {quality ? (
            <div className="cwt-chart__body">
              <div className="cwt-qbars">
                <div className="cwt-qbar">
                  <span className="cwt-qbar__label">Initial</span>
                  <span className="cwt-qbar__track">
                    <span
                      className="cwt-qbar__fill cwt-qbar__fill--initial"
                      style={{ width: `${quality.initialPercent}%` }}
                    />
                  </span>
                  <span className="cwt-qbar__value">{formatScore(quality.initial)}</span>
                </div>
                <div className="cwt-qbar">
                  <span className="cwt-qbar__label">Best</span>
                  <span className="cwt-qbar__track">
                    <span
                      className={`cwt-qbar__fill cwt-qbar__fill--best cwt-qbar__fill--${quality.direction}`}
                      style={{ width: `${quality.bestPercent}%` }}
                    />
                  </span>
                  <span className="cwt-qbar__value">{formatScore(quality.best)}</span>
                </div>
              </div>
              <div className={`cwt-qdelta cwt-qdelta--${quality.direction}`}>
                <i className={`fas ${DIRECTION_ICON[quality.direction]}`} aria-hidden="true" />
                <strong>{formatSigned(quality.delta)}</strong>
                {quality.relative != null ? (
                  <span>{formatSigned(quality.relative * 100, 1)}% vs baseline</span>
                ) : (
                  <span>vs baseline</span>
                )}
              </div>
              {VERDICT_NOTE[verdict] ? <p className="cwt-chart__note">{VERDICT_NOTE[verdict]}</p> : null}
            </div>
          ) : (
            <NoData>
              This run carries no <code>scores</code> — they arrive with the finished job, not while it runs.
            </NoData>
          )}
        </article>

        <article className="cwt-chart" data-testid="cwt-chart-work">
          <header className="cwt-chart__head">
            <h4>Where the work landed</h4>
            <span className="cwt-chart__hint">
              {bars.length} slices · {work.pathsChanged.toLocaleString()} of{' '}
              {work.pathsTotal.toLocaleString()} paths
            </span>
          </header>
          {bars.length ? (
            <div className="cwt-chart__body">
              <ul className={`cwt-slices ${bars.length > VISIBLE_ROWS ? 'cwt-rows--more' : ''}`}>
                {bars.map((bar) => (
                  <li
                    key={bar.slice}
                    className={`cwt-slice ${bar.isGlobals ? 'cwt-slice--globals' : ''} ${
                      bar.failed ? 'cwt-slice--failed' : ''
                    }`}
                  >
                    <span className="cwt-slice__name" title={bar.slice}>
                      {bar.isGlobals ? <i className="fas fa-globe" aria-hidden="true" /> : null}
                      <span className="cwt-slice__text">{bar.label}</span>
                    </span>
                    <span className="cwt-slice__track">
                      <span className="cwt-slice__fill" style={{ width: `${Math.round(bar.ratio * 100)}%` }} />
                    </span>
                    <span className="cwt-slice__value">
                      {bar.changed}/{bar.paths}
                    </span>
                    {bar.lost > 0 ? (
                      <Pill tone="warn" title="Path keys the model mangled beyond recovery">
                        {bar.lost}
                      </Pill>
                    ) : null}
                  </li>
                ))}
              </ul>
              <p className="cwt-chart__note">
                {work.statesTouched} of {work.statesTotal} states changed
                {work.lost > 0 ? ` · ${work.lost} paths lost to mangled keys` : ''}
                {work.failedSlices > 0 ? ` · ${work.failedSlices} slices errored` : ''}. Proposed, not yet applied.
              </p>
            </div>
          ) : (
            <NoData>The journal has no slice iterations for this run.</NoData>
          )}
        </article>

        <article className="cwt-chart" data-testid="cwt-chart-spend">
          <header className="cwt-chart__head">
            <h4>Token spend</h4>
            {split ? (
              <span className="cwt-chart__legend">
                <span className="cwt-chart__key cwt-chart__key--prompt" /> prompt
                <span className="cwt-chart__key cwt-chart__key--completion" /> completion
              </span>
            ) : (
              <span className="cwt-chart__hint">per slice</span>
            )}
          </header>
          {bars.length && peak > 0 ? (
            <div className="cwt-chart__body">
              <ul className={`cwt-spend ${bars.length > VISIBLE_ROWS ? 'cwt-rows--more' : ''}`}>
                {bars.map((bar) => (
                  <li key={bar.slice} className="cwt-spend__row">
                    <span className="cwt-spend__name" title={bar.slice}>
                      <span className="cwt-slice__text">{bar.label}</span>
                    </span>
                    <span className="cwt-spend__track">
                      {split ? (
                        <>
                          <span
                            className="cwt-spend__seg cwt-spend__seg--prompt"
                            style={{ width: `${(bar.promptTokens / peak) * 100}%` }}
                          />
                          <span
                            className="cwt-spend__seg cwt-spend__seg--completion"
                            style={{ width: `${(bar.completionTokens / peak) * 100}%` }}
                          />
                        </>
                      ) : (
                        <span
                          className="cwt-spend__seg cwt-spend__seg--total"
                          style={{ width: `${(bar.tokens / peak) * 100}%` }}
                        />
                      )}
                    </span>
                    <span className="cwt-spend__value">{formatCompactTokens(bar.tokens)}</span>
                  </li>
                ))}
              </ul>
              <p className="cwt-chart__note">
                {spent.toLocaleString()} tokens across {bars.length} slice{bars.length === 1 ? '' : 's'}
                {totalTokens > spent
                  ? ` — the run's ${totalTokens.toLocaleString()} total also covers evaluation and selection.`
                  : '.'}
              </p>
            </div>
          ) : (
            <NoData>No per-slice token counts in this run's journal.</NoData>
          )}
        </article>

        {evalPoints.length > 0 ? (
          <article className="cwt-chart cwt-chart--wide" data-testid="cwt-chart-trend">
            <header className="cwt-chart__head">
              <h4>Evaluation runs</h4>
              <span className="cwt-chart__legend">
                {typeof job?.scores?.best === 'number' ? (
                  <>
                    <span className="cwt-chart__key cwt-chart__key--ref" /> best of this run ·
                  </>
                ) : null}
                <span className="cwt-chart__hint">
                  {evalPoints.length} scored run{evalPoints.length === 1 ? '' : 's'}
                </span>
              </span>
            </header>
            <div className="cwt-chart__body">
              <EvaluationTrend points={evalPoints} best={job?.scores?.best} />
              <p className="cwt-chart__note">
                Quality of each evaluation run on this integration, oldest first. There is no curve{' '}
                <em>inside</em> a tuning run: the job reports one score for the whole run, not one per iteration.
              </p>
            </div>
          </article>
        ) : null}
      </div>
    </section>
  );
};

export default WorkflowTuningCharts;
